import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Typography,
  OutlinedInput,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  FilterList,
  Clear,
  Search,
  Delete,
  Add,
  DateRange,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CustomFilterModal = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter,
  data = [],
  sortableColumns = [],
}) => {
  const theme = useTheme();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJabatan, setSelectedJabatan] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState([]);

  // Date Range Filter
  const [tanggalAwal, setTanggalAwal] = useState(null);
  const [tanggalAkhir, setTanggalAkhir] = useState(null);

  // Sort state: array of { column, order }
  const [sortCriteria, setSortCriteria] = useState([
    { column: "", order: "asc" },
  ]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedJabatan([]);
    setSelectedCabang([]);
    setTanggalAwal(null);
    setTanggalAkhir(null);
    setSortCriteria([{ column: "", order: "asc" }]);
    onClearFilter();
  };

  const getUniqueJabatan = () => {
    return [
      ...new Set(
        data
          .map((row) => row.nama_jabatan)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  const getUniqueCabang = () => {
    return [
      ...new Set(
        data
          .map((row) => row.cabang)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  // Hanya tampilkan kolom yang belum dipilih
  const getAvailableColumns = (currentIndex) => {
    const usedColumns = sortCriteria
      .filter((_, idx) => idx !== currentIndex)
      .map((c) => c.column);
    return sortableColumns.filter((col) => !usedColumns.includes(col.value));
  };

  const handleSortChange = (index, key, value) => {
    const updated = [...sortCriteria];
    updated[index][key] = value;
    setSortCriteria(updated);
  };

  const addSortCriteria = () => {
    setSortCriteria([...sortCriteria, { column: "", order: "asc" }]);
  };

  const removeSortCriteria = (index) => {
    const updated = [...sortCriteria];
    updated.splice(index, 1);
    setSortCriteria(updated.length ? updated : [{ column: "", order: "asc" }]);
  };

  const applyFilters = () => {
    const filterConfig = {
      search: searchQuery,
      filter: {
        cabang: selectedCabang,
        nama_jabatan: selectedJabatan,
      },
      dateRange: {
        tanggalAwal: tanggalAwal ? formatDateToYYYYMMDD(tanggalAwal) : null,
        tanggalAkhir: tanggalAkhir ? formatDateToYYYYMMDD(tanggalAkhir) : null,
      },
      sort: sortCriteria.filter((c) => c.column), // hanya kirim kriteria valid
    };
    onApplyFilter(filterConfig);
    onClose();
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedJabatan.length > 0) count++;
    if (selectedCabang.length > 0) count++;
    if (tanggalAwal || tanggalAkhir) count++;
    if (sortCriteria.some((c) => c.column)) count++;
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <FilterList sx={{ mr: 1 }} />
            <Typography variant="h6">Filter</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {/* DATE RANGE FILTER */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <DateRange sx={{ mr: 1 }} /> Filter Tanggal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  Tanggal Awal
                </Typography>
                <DatePicker
                  selected={tanggalAwal}
                  onChange={(date) => setTanggalAwal(date)}
                  selectsStart
                  startDate={tanggalAwal}
                  endDate={tanggalAkhir}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Pilih tanggal awal"
                  customInput={
                    <TextField
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <DateRange sx={{ color: "text.secondary" }} />
                        ),
                      }}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  Tanggal Akhir
                </Typography>
                <DatePicker
                  selected={tanggalAkhir}
                  onChange={(date) => setTanggalAkhir(date)}
                  selectsEnd
                  startDate={tanggalAwal}
                  endDate={tanggalAkhir}
                  minDate={tanggalAwal}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Pilih tanggal akhir"
                  customInput={
                    <TextField
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <DateRange sx={{ color: "text.secondary" }} />
                        ),
                      }}
                    />
                  }
                  locale="id"
                />
              </Box>
            </Grid>
          </Grid>

          {(tanggalAwal || tanggalAkhir) && (
            <Box
              sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}
              >
                📅 Filter Tanggal Aktif:
              </Typography>
              <Typography variant="body2">
                {tanggalAwal
                  ? tanggalAwal.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "..."}
                {" → "}
                {tanggalAkhir
                  ? tanggalAkhir.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "..."}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* FILTER */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            🧭 Filter Kategori
          </Typography>
          <Grid container spacing={2}>
            {/* Jabatan */}
            {getUniqueJabatan().length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter Jabatan</InputLabel>
                  <Select
                    multiple
                    value={selectedJabatan}
                    onChange={(e) =>
                      setSelectedJabatan(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value
                      )
                    }
                    input={<OutlinedInput label="Filter Jabatan" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {getUniqueJabatan().map((jabatan) => (
                      <MenuItem key={jabatan} value={jabatan}>
                        <Checkbox
                          checked={selectedJabatan.indexOf(jabatan) > -1}
                        />
                        <ListItemText primary={jabatan} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Cabang */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Cabang</InputLabel>
                <Select
                  multiple
                  value={selectedCabang}
                  onChange={(e) =>
                    setSelectedCabang(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Cabang" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueCabang().map((cabang) => (
                    <MenuItem key={cabang} value={cabang}>
                      <Checkbox checked={selectedCabang.indexOf(cabang) > -1} />
                      <ListItemText primary={cabang} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={clearAllFilters}
          startIcon={<Clear />}
          variant="outlined"
          color="secondary"
        >
          Clear All
        </Button>
        <Box display="flex" gap={1}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={applyFilters}
            variant="contained"
            startIcon={<FilterList />}
            color="primary"
          >
            Apply {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomFilterModal;
